/**
 * Centralized Profile Service
 * 
 * Single source of truth for all profile data operations.
 * Handles type mapping between camelCase (TypeScript) ↔ snake_case (Supabase).
 * All queries are scoped to authenticated user for security.
 */

import { supabase } from '../config/supabase';
import { UserProfile, GolfClub, ClubDistance } from '../types';

/**
 * Convert TypeScript profile to Supabase format
 * Maps camelCase → snake_case
 */
export function profileToDatabase(profile: UserProfile): any {
  // Safely convert numeric values - ensures decimals are preserved and non-numbers become null
  const safeNumber = (val: any): number | null => {
    if (val === null || val === undefined) return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
  };
  const safeInt = (val: any): number | null => {
    if (val === null || val === undefined) return null;
    const num = Number(val);
    return isNaN(num) ? null : Math.round(num);
  };

  return {
    id: profile.id,
    email: profile.email,
    first_name: profile.firstName,
    last_name: profile.lastName,
    handicap: safeNumber(profile.handicap),          // numeric(4,1) - supports decimals like 3.8
    height: safeInt(profile.height),
    weight: safeInt(profile.weight),
    birthdate: profile.birthdate || null,
    age: safeInt(profile.age),
    // Golf Persona System
    average_score: safeInt(profile.averageScore) || null,
    golf_persona: profile.golfPersona || null,
    // Swing data
    swing_speed: safeNumber(profile.swingSpeed) || null, // numeric(5,1) - supports decimals
    swing_speed_range: profile.swingSpeedRange || null,
    swing_tempo: profile.swingTempo,
    ball_flight: profile.ballFlight,
    // Advanced swing data
    hand_size: profile.handSize || null,
    grip_type: profile.gripType || null,
    grip_pressure: profile.gripPressure || null,
    attack_angle: profile.attackAngle || null,
    // Profile data
    skill_level: profile.skillLevel,
    playing_experience: profile.playingExperience || null,
    course_frequency: profile.courseFrequency || null,
    miss_pattern: profile.missPattern || null,
    preferred_course_type: profile.preferredCourseType || null,
    golf_goals: profile.golfGoals || [],
    current_issues: profile.currentIssues || [],
    // Equipment
    club_distances: profile.clubDistances || [],
    golf_bag: profile.golfBag || [],
    preferred_units: profile.preferredUnits || 'yards',
    has_completed_onboarding: profile.hasCompletedOnboarding || false,
    ...(profile.onboardingSkippedAt
      ? { onboarding_skipped_at: profile.onboardingSkippedAt.toISOString() }
      : {}),
    // Equipment Preferences
    equipment_tier: profile.equipmentTier || null,
    // Referral System — NEVER overwrite with null if not explicitly set.
    // referral_code is generated separately via ensureReferralCode() and
    // must not be wiped by a profile save that doesn't include it.
    ...(profile.referralCode ? { referral_code: profile.referralCode } : {}),
    updated_at: new Date().toISOString(),
  };
}

/**
 * Convert Supabase profile to TypeScript format
 * Maps snake_case → camelCase
 */
export function profileFromDatabase(dbProfile: any): UserProfile {
  if (!dbProfile) {
    throw new Error('No profile data provided');
  }

  // Validate required fields
  if (!dbProfile.id || !dbProfile.first_name || !dbProfile.last_name || !dbProfile.email) {
    throw new Error('Profile missing required fields');
  }

  // CRITICAL: Log raw database values for debugging
  console.log('🔍 [profileFromDatabase] Raw DB values:', {
    has_completed_onboarding: dbProfile.has_completed_onboarding,
    golf_bag_length: dbProfile.golf_bag?.length || 0,
    club_distances_length: dbProfile.club_distances?.length || 0,
    average_score: dbProfile.average_score
  });

  const sanitizedGolfBag = Array.isArray(dbProfile.golf_bag) ? dbProfile.golf_bag : [];
  const sanitizedClubDistances = Array.isArray(dbProfile.club_distances) ? dbProfile.club_distances : [];
  const hasCompletedFlag = dbProfile.has_completed_onboarding;
  const hasCompletedOnboarding =
    hasCompletedFlag === true ||
    hasCompletedFlag === 'true' ||
    hasCompletedFlag === 't' ||
    hasCompletedFlag === 1 ||
    hasCompletedFlag === '1';

  return {
    id: dbProfile.id,
    email: dbProfile.email,
    firstName: dbProfile.first_name,
    lastName: dbProfile.last_name,
    // Use ?? (nullish coalescing) for numeric fields so 0 is preserved (e.g., scratch golfer handicap = 0)
    handicap: dbProfile.handicap ?? 18,
    height: dbProfile.height ?? 70,
    weight: dbProfile.weight ?? 180,
    birthdate: dbProfile.birthdate ?? undefined,
    age: dbProfile.age ?? 35,
    // Golf Persona System
    averageScore: dbProfile.average_score ?? undefined,
    golfPersona: dbProfile.golf_persona ?? undefined,
    // Swing data
    swingSpeed: dbProfile.swing_speed ?? undefined,
    swingSpeedRange: dbProfile.swing_speed_range ?? undefined,
    swingTempo: dbProfile.swing_tempo ?? 'medium',
    ballFlight: dbProfile.ball_flight ?? 'straight',
    // Advanced swing data
    handSize: dbProfile.hand_size ?? undefined,
    gripType: dbProfile.grip_type ?? undefined,
    gripPressure: dbProfile.grip_pressure ?? undefined,
    attackAngle: dbProfile.attack_angle ?? undefined,
    // Profile data
    skillLevel: dbProfile.skill_level ?? 'intermediate',
    playingExperience: dbProfile.playing_experience ?? 'casual',
    courseFrequency: dbProfile.course_frequency ?? 'monthly',
    missPattern: dbProfile.miss_pattern ?? undefined,
    preferredCourseType: dbProfile.preferred_course_type ?? undefined,
    golfGoals: dbProfile.golf_goals ?? [],
    currentIssues: dbProfile.current_issues ?? [],
    // Equipment
    clubDistances: sanitizedClubDistances as ClubDistance[],
    golfBag: sanitizedGolfBag as GolfClub[],
    preferredUnits: dbProfile.preferred_units ?? 'yards',
    // CRITICAL FIX: Use strict equality to preserve true values
    hasCompletedOnboarding,
    onboardingSkippedAt: dbProfile.onboarding_skipped_at
      ? new Date(dbProfile.onboarding_skipped_at)
      : undefined,
    // Equipment Preferences
    equipmentTier: dbProfile.equipment_tier ?? undefined,
    // Referral System
    referralCode: dbProfile.referral_code ?? undefined,
    referredBy: dbProfile.referred_by ?? undefined,
    createdAt: new Date(dbProfile.created_at),
    updatedAt: new Date(dbProfile.updated_at),
  };
}

/**
 * Get profile for authenticated user
 */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  try {
    console.log('📥 [profileService] Fetching profile for user:', userId);

    // Verify authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.error('❌ [profileService] No authenticated session');
      throw new Error('Not authenticated');
    }

    // Security: Ensure we're fetching the authenticated user's profile
    if (session.user.id !== userId) {
      console.error('❌ [profileService] User ID mismatch');
      throw new Error('Unauthorized');
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId) // CRITICAL: Always scope to specific user
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('ℹ️ [profileService] Profile not found (expected for new users)');
        return null;
      }
      console.error('❌ [profileService] Error fetching profile:', error);
      throw error;
    }

    console.log('✅ [profileService] Profile fetched successfully');
    return profileFromDatabase(data);
  } catch (error) {
    console.error('💥 [profileService] getProfile error:', error);
    throw error;
  }
}

/**
 * Update profile for authenticated user
 * ALWAYS scoped to userId - prevents cross-pollution
 */
export async function updateProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<UserProfile> {
  try {
    console.log('💾 [profileService] Updating profile for user:', userId);

    // Verify authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.error('❌ [profileService] No authenticated session');
      throw new Error('Not authenticated');
    }

    // Security: Ensure we're updating the authenticated user's profile
    if (session.user.id !== userId) {
      console.error('❌ [profileService] User ID mismatch');
      throw new Error('Unauthorized');
    }

    // Validate required fields if they're being updated
    if (updates.firstName !== undefined && !updates.firstName) {
      throw new Error('First name is required');
    }
    if (updates.lastName !== undefined && !updates.lastName) {
      throw new Error('Last name is required');
    }
    if (updates.email !== undefined && !updates.email) {
      throw new Error('Email is required');
    }

    // Convert to database format (only the fields being updated)
    const dbUpdates: any = {
      updated_at: new Date().toISOString(),
    };

    // Map each field if provided
    if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.handicap !== undefined) dbUpdates.handicap = updates.handicap;
    if (updates.height !== undefined) dbUpdates.height = updates.height;
    if (updates.weight !== undefined) dbUpdates.weight = updates.weight;
    if (updates.birthdate !== undefined) dbUpdates.birthdate = updates.birthdate;
    if (updates.age !== undefined) dbUpdates.age = updates.age;
    // Golf Persona System
    if (updates.averageScore !== undefined) dbUpdates.average_score = updates.averageScore;
    if (updates.golfPersona !== undefined) dbUpdates.golf_persona = updates.golfPersona;
    // Swing data
    if (updates.swingSpeed !== undefined) dbUpdates.swing_speed = updates.swingSpeed;
    if (updates.swingSpeedRange !== undefined) dbUpdates.swing_speed_range = updates.swingSpeedRange;
    if (updates.swingTempo !== undefined) dbUpdates.swing_tempo = updates.swingTempo;
    if (updates.ballFlight !== undefined) dbUpdates.ball_flight = updates.ballFlight;
    // Advanced swing data
    if (updates.handSize !== undefined) dbUpdates.hand_size = updates.handSize;
    if (updates.gripType !== undefined) dbUpdates.grip_type = updates.gripType;
    if (updates.gripPressure !== undefined) dbUpdates.grip_pressure = updates.gripPressure;
    if (updates.attackAngle !== undefined) dbUpdates.attack_angle = updates.attackAngle;
    // Profile data
    if (updates.skillLevel !== undefined) dbUpdates.skill_level = updates.skillLevel;
    if (updates.playingExperience !== undefined) dbUpdates.playing_experience = updates.playingExperience;
    if (updates.courseFrequency !== undefined) dbUpdates.course_frequency = updates.courseFrequency;
    if (updates.missPattern !== undefined) dbUpdates.miss_pattern = updates.missPattern;
    if (updates.preferredCourseType !== undefined) dbUpdates.preferred_course_type = updates.preferredCourseType;
    if (updates.golfGoals !== undefined) dbUpdates.golf_goals = updates.golfGoals;
    if (updates.currentIssues !== undefined) dbUpdates.current_issues = updates.currentIssues;
    // Equipment
    if (updates.golfBag !== undefined) dbUpdates.golf_bag = updates.golfBag;
    if (updates.clubDistances !== undefined) dbUpdates.club_distances = updates.clubDistances;
    if (updates.preferredUnits !== undefined) dbUpdates.preferred_units = updates.preferredUnits;
    if (updates.hasCompletedOnboarding !== undefined) dbUpdates.has_completed_onboarding = updates.hasCompletedOnboarding;
    if (updates.onboardingSkippedAt !== undefined) {
      dbUpdates.onboarding_skipped_at = updates.onboardingSkippedAt
        ? new Date(updates.onboardingSkippedAt).toISOString()
        : null;
    }
    // Equipment Preferences
    if (updates.equipmentTier !== undefined) dbUpdates.equipment_tier = updates.equipmentTier;
    // Referral System
    if (updates.referralCode !== undefined) dbUpdates.referral_code = updates.referralCode;
    if (updates.referredBy !== undefined) dbUpdates.referred_by = updates.referredBy;

    console.log('🔄 [profileService] Database updates:', {
      userId,
      fieldsUpdated: Object.keys(dbUpdates),
      golfBagLength: dbUpdates.golf_bag?.length,
      clubDistancesLength: dbUpdates.club_distances?.length,
    });

    // CRITICAL: Use .eq() to scope update to specific user
    const { data, error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', userId) // ALWAYS scope to user ID
      .select()
      .single();

    if (error) {
      console.error('❌ [profileService] Update error:', error);
      throw error;
    }

    if (!data) {
      console.error('❌ [profileService] No data returned from update');
      throw new Error('Update failed - no data returned');
    }

    console.log('✅ [profileService] Profile updated successfully');
    return profileFromDatabase(data);
  } catch (error) {
    console.error('💥 [profileService] updateProfile error:', error);
    throw error;
  }
}

/**
 * Upsert profile (create or update)
 * Use for onboarding when profile might not exist yet
 */
export async function upsertProfile(profile: UserProfile): Promise<UserProfile> {
  try {
    console.log('💾 [profileService] Upserting profile for user:', profile.id);

    // Verify authentication
    const { data: { session } } = await supabase.auth.getSession();
    
    // Allow upsert if session exists OR if we're in the signup flow
    // (session might not be fully established during onboarding)
    if (session?.user) {
      // If we have a session, verify it matches the profile ID
      if (session.user.id !== profile.id) {
        console.error('❌ [profileService] User ID mismatch');
        throw new Error('Unauthorized');
      }
      console.log('✅ [profileService] Session verified for user:', session.user.email);
    } else {
      // No session - this can happen during initial signup/onboarding
      // RLS policies will still enforce that only the user can update their own profile
      console.warn('⚠️ [profileService] No session found, relying on RLS policies for security');
    }

    // Validate required fields
    if (!profile.firstName || !profile.lastName || !profile.email) {
      throw new Error('First name, last name, and email are required');
    }

    const dbProfile = profileToDatabase(profile);

    console.log('🔄 [profileService] Upserting with data:', {
      userId: profile.id,
      golfBagLength: dbProfile.golf_bag?.length,
      clubDistancesLength: dbProfile.club_distances?.length,
      hasCompletedOnboarding: dbProfile.has_completed_onboarding,
    });

    // CRITICAL: Use onConflict to specify unique key
    const { data, error } = await supabase
      .from('profiles')
      .upsert(dbProfile, {
        onConflict: 'id', // CRITICAL: Prevents cross-pollution
      })
      .select()
      .single();

    if (error) {
      console.error('❌ [profileService] Upsert error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }

    if (!data) {
      console.error('❌ [profileService] No data returned from upsert');
      throw new Error('Upsert failed - no data returned');
    }

    console.log('✅ [profileService] Profile upserted successfully');
    return profileFromDatabase(data);
  } catch (error) {
    console.error('💥 [profileService] upsertProfile error:', error);
    throw error;
  }
}

/**
 * Update only golf bag
 * Convenience method for common operation
 */
export async function updateGolfBag(
  userId: string,
  golfBag: GolfClub[]
): Promise<UserProfile> {
  console.log('🎒 [profileService] Updating golf bag:', {
    userId,
    clubCount: golfBag.length,
  });

  return updateProfile(userId, { golfBag });
}

/**
 * Update only club distances
 * Convenience method for common operation
 */
export async function updateClubDistances(
  userId: string,
  clubDistances: ClubDistance[]
): Promise<UserProfile> {
  console.log('📏 [profileService] Updating club distances:', {
    userId,
    distanceCount: clubDistances.length,
  });

  return updateProfile(userId, { clubDistances });
}

/**
 * Verify profile data after critical saves
 * Used to ensure onboarding data was saved correctly
 */
export async function verifyProfileSave(userId: string): Promise<{
  success: boolean;
  golfBagSaved: boolean;
  distancesSaved: boolean;
  onboardingComplete: boolean;
  issues: string[];
}> {
  try {
    console.log('🔍 [profileService] Verifying profile save for user:', userId);

    const { data, error } = await supabase
      .from('profiles')
      .select('golf_bag, club_distances, has_completed_onboarding')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ [profileService] Verification error:', error);
      return {
        success: false,
        golfBagSaved: false,
        distancesSaved: false,
        onboardingComplete: false,
        issues: [error.message],
      };
    }

    const golfBagSaved = Array.isArray(data.golf_bag) && data.golf_bag.length > 0;
    const distancesSaved = Array.isArray(data.club_distances) && data.club_distances.length > 0;
    const onboardingComplete = data.has_completed_onboarding === true;

    const issues: string[] = [];
    if (!golfBagSaved) issues.push('Golf bag is empty');
    if (!distancesSaved) issues.push('Club distances are empty');
    if (!onboardingComplete) issues.push('Onboarding not marked complete');

    const success = golfBagSaved && distancesSaved && onboardingComplete;

    console.log('🔍 [profileService] Verification result:', {
      success,
      golfBagSaved,
      distancesSaved,
      onboardingComplete,
      issues,
    });

    return {
      success,
      golfBagSaved,
      distancesSaved,
      onboardingComplete,
      issues,
    };
  } catch (error) {
    console.error('💥 [profileService] verifyProfileSave error:', error);
    return {
      success: false,
      golfBagSaved: false,
      distancesSaved: false,
      onboardingComplete: false,
      issues: [String(error)],
    };
  }
}

/**
 * Delete profile (for cleanup/testing)
 * Only use in development or user-requested deletion
 */
export async function deleteProfile(userId: string): Promise<void> {
  try {
    console.log('🗑️ [profileService] Deleting profile for user:', userId);

    // Verify authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Not authenticated');
    }

    // Security: Ensure we're deleting the authenticated user's profile
    if (session.user.id !== userId) {
      throw new Error('Unauthorized');
    }

    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId); // ALWAYS scope to user ID

    if (error) {
      console.error('❌ [profileService] Delete error:', error);
      throw error;
    }

    console.log('✅ [profileService] Profile deleted successfully');
  } catch (error) {
    console.error('💥 [profileService] deleteProfile error:', error);
    throw error;
  }
}

// ============================================================================
// REFERRAL SYSTEM
// ============================================================================

/**
 * Generate a unique referral code for a user
 * Format: First 4 letters of name (uppercase) + 4 random alphanumeric chars
 * Example: "DOUG7X3K"
 */
export function generateReferralCode(firstName: string): string {
  const namePrefix = firstName
    .toUpperCase()
    .replace(/[^A-Z]/g, '') // Remove non-letters
    .substring(0, 4)
    .padEnd(4, 'X'); // Pad with X if name is short
  
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing chars (0, O, 1, I)
  let randomSuffix = '';
  for (let i = 0; i < 4; i++) {
    randomSuffix += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return `${namePrefix}${randomSuffix}`;
}

/**
 * Check if a referral code exists and return the referrer's user ID
 */
export async function validateReferralCode(referralCode: string): Promise<string | null> {
  try {
    const code = referralCode.toUpperCase().trim();
    console.log('🔍 [profileService] Validating referral code:', code);

    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('referral_code', code)
      .single();

    if (error || !data) {
      console.log('❌ [profileService] Referral code not found:', code);
      return null;
    }

    console.log('✅ [profileService] Referral code valid, referrer:', data.id);
    return data.id;
  } catch (error) {
    console.error('💥 [profileService] validateReferralCode error:', error);
    return null;
  }
}

/**
 * Apply a referral code for a new user
 * Creates a record in the referrals table linking referrer and referred
 */
export async function applyReferralCode(
  referralCode: string,
  newUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const code = referralCode.toUpperCase().trim();
    console.log('🎯 [profileService] Applying referral code:', code, 'for user:', newUserId);

    const { data, error } = await supabase.rpc('apply_user_referral', { p_code: code });
    if (error) {
      console.error('❌ [profileService] apply_user_referral failed:', error);
      return { success: false, error: 'Failed to record referral' };
    }
    if (data && data.success === false) {
      const reason = data.error;
      if (reason === 'self_referral') return { success: false, error: 'Cannot use your own referral code' };
      if (reason === 'already_attributed') return { success: false, error: 'Already referred by another user' };
      return { success: false, error: 'Invalid referral code' };
    }
    console.log('✅ [profileService] Referral applied via server RPC');
    return { success: true };
  } catch (error) {
    console.error('💥 [profileService] applyReferralCode error:', error);
    return { success: false, error: 'An error occurred' };
  }
}

/**
 * Get the count of successful referrals for a user
 * This represents the number of extra giveaway entries they've earned
 */
export async function getReferralCount(userId: string): Promise<number> {
  try {
    console.log('📊 [profileService] Getting referral count for user:', userId);

    const { count, error } = await supabase
      .from('referrals')
      .select('*', { count: 'exact', head: true })
      .eq('referrer_id', userId);

    if (error) {
      console.error('❌ [profileService] Error getting referral count:', error);
      return 0;
    }

    const referralCount = count || 0;
    console.log('✅ [profileService] Referral count:', referralCount);
    return referralCount;
  } catch (error) {
    console.error('💥 [profileService] getReferralCount error:', error);
    return 0;
  }
}

/**
 * Get list of users referred by this user
 * For display in the user's profile
 */
export async function getReferrals(userId: string): Promise<{
  id: string;
  firstName: string;
  createdAt: Date;
}[]> {
  try {
    console.log('📋 [profileService] Getting referrals for user:', userId);

    const { data, error } = await supabase
      .from('referrals')
      .select(`
        referred_id,
        created_at,
        profiles!referrals_referred_id_fkey (first_name)
      `)
      .eq('referrer_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ [profileService] Error getting referrals:', error);
      return [];
    }

    // @ts-ignore - Supabase join typing
    return (data || []).map((r: any) => ({
      id: r.referred_id,
      firstName: r.profiles?.first_name || 'Anonymous',
      createdAt: new Date(r.created_at),
    }));
  } catch (error) {
    console.error('💥 [profileService] getReferrals error:', error);
    return [];
  }
}

/**
 * Ensure a user has a referral code
 * Generates one if they don't have one yet
 */
export async function ensureReferralCode(
  userId: string,
  firstName: string
): Promise<string> {
  try {
    console.log('🔑 [profileService] Ensuring referral code for user:', userId);

    // Check if user already has a code
    const { data: profile } = await supabase
      .from('profiles')
      .select('referral_code')
      .eq('id', userId)
      .single();

    if (profile?.referral_code) {
      console.log('✅ [profileService] User already has referral code:', profile.referral_code);
      return profile.referral_code;
    }

    // Generate a new code and ensure uniqueness
    let code = generateReferralCode(firstName);
    let isUnique = false;
    const maxAttempts = 10;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('referral_code', code)
        .single();

      if (!existing) {
        isUnique = true;
        break; // Code is unique
      }

      // Generate a new code for next attempt
      code = generateReferralCode(firstName);
    }

    if (!isUnique) {
      throw new Error('Failed to generate unique referral code after ' + maxAttempts + ' attempts');
    }

    // Save the code - use .select() to verify the update actually happened
    const { data: updatedProfile, error } = await supabase
      .from('profiles')
      .update({ referral_code: code })
      .eq('id', userId)
      .select('referral_code')
      .single();

    if (error) {
      console.error('❌ [profileService] Failed to save referral code:', error);
      throw error;
    }

    // Verify the update actually worked (RLS might silently block it)
    if (!updatedProfile || updatedProfile.referral_code !== code) {
      console.error('❌ [profileService] Referral code not saved - RLS may be blocking update');
      throw new Error('Failed to save referral code - permission denied');
    }

    console.log('✅ [profileService] Generated new referral code:', code);
    return code;
  } catch (error) {
    console.error('💥 [profileService] ensureReferralCode error:', error);
    throw error;
  }
}

